import { Injectable, inject } from "@angular/core";
import { ModalController, ModalOptions } from "@ionic/angular";

import { MAX_MOBILE_WIDTH } from "src/app/constants";

import { UtilsService } from "../utils/utils.service";

// Inside an iframe widget, window.innerHeight reflects the iframe's height — which
// can be larger than the user's actually visible viewport on the host page. Clamp
// sheet breakpoints so modals don't overflow the visible area, while keeping enough
// room for content on mobile widgets where iframe ≈ device viewport.
const IFRAME_INITIAL_BREAKPOINT_CAP = 0.7;
const IFRAME_MAX_BREAKPOINT_CAP = 0.9;

@Injectable({ providedIn: "root" })
export class ModalLauncherService {
  private readonly utilsService = inject(UtilsService);
  private readonly modalController = inject(ModalController);

  /**
   * Returns responsive modal options based on screen width, device type, and touch capability.
   *
   * - Non-touch screens on small screens: Uses single breakpoint at 0.75 (no handle)
   * - Non-touch screens on desktop/wide: Removes breakpoints, uses centered modal
   * - Touch screens on mobile/narrow: Uses breakpoints for bottom sheet behavior
   * - Touch screens on desktop/wide: Can use breakpoints if provided
   *
   * @param options - The base modal options to configure
   * @returns Modal options configured for the current screen size and device capabilities
   */
  public getResponsiveModalOptions(options: ModalOptions<any>): ModalOptions<any> {
    return this.applyIframeConstraints(this.computeResponsiveOptions(options));
  }

  private computeResponsiveOptions(options: ModalOptions<any>): ModalOptions<any> {
    const isMobile = this.utilsService.isMobileDevice();
    const isTouchScreen = this.utilsService.isTouchScreen();
    const isWideEnough = window.innerWidth > MAX_MOBILE_WIDTH;
    const shouldUseDesktopModal = !isMobile && isWideEnough;

    // Non-touch screens on small screens: use single breakpoint at 0.75 (no handle)
    if (!isTouchScreen && !shouldUseDesktopModal) {
      return {
        ...options,
        breakpoints: [0, 0.9],
        initialBreakpoint: 0.9,
        handle: false,
      };
    }

    // Non-touch screens on desktop/wide: remove breakpoints, use centered modal
    if (!isTouchScreen) {
      const { breakpoints, initialBreakpoint, expandToScroll, ...rest } = options;
      return rest;
    }

    // Touch screens: use breakpoints on mobile/narrow screens
    if (!shouldUseDesktopModal) {
      return {
        ...options,
        breakpoints: options.breakpoints ?? [0, 0.5, 0.75, 1],
        initialBreakpoint: options.initialBreakpoint ?? 0.75,
        handle: options.handle !== undefined ? options.handle : true,
      };
    }

    // Touch screens on desktop/wide: use breakpoints if provided
    const { breakpoints, initialBreakpoint, expandToScroll, ...rest } = options;
    return {
      ...rest,
      breakpoints,
      initialBreakpoint,
      expandToScroll,
      handle: options.handle !== undefined ? options.handle : true,
    };
  }

  private applyIframeConstraints(options: ModalOptions<any>): ModalOptions<any> {
    if (!this.utilsService.isInIframe()) return options;
    if (!options.breakpoints || options.initialBreakpoint == null) return options;

    const scaledInitial = Math.min(options.initialBreakpoint, IFRAME_INITIAL_BREAKPOINT_CAP);
    const lastBreakpoint = options.breakpoints[options.breakpoints.length - 1] ?? scaledInitial;
    const scaledMax = Math.min(lastBreakpoint, IFRAME_MAX_BREAKPOINT_CAP);

    return {
      ...options,
      initialBreakpoint: scaledInitial,
      breakpoints: Array.from(new Set([0, scaledInitial, scaledMax])).sort((a, b) => a - b),
    };
  }

  public async present(options: ModalOptions<any>): Promise<void> {
    const responsiveOptions = this.getResponsiveModalOptions(options);
    const modal = await this.modalController.create(responsiveOptions);
    await modal.present();
  }

  public computeSheetBreakpoint({
    cap = 0.6,
    maxHeight = 500,
    maxVhRatio = 0.8,
  }: { cap?: number; maxHeight?: number; maxVhRatio?: number } = {}): number {
    return Math.min(cap, maxHeight / window.innerHeight, maxVhRatio);
  }
}
